<template>
  <div v-if="text" class="s-collapse-text">
    <div class="s-collapse-text__content">
      <p
        ref="truncatedTextEl"
        class="s-collapse-text__content--truncated"
        v-html="text"
      />
      <transition
        name="s-collapse-resize"
        @after-enter="afterEnter"
        @after-leave="afterLeave"
        @enter="enter"
        @leave="leave"
      >
        <p v-if="expanded" v-html="text" />
      </transition>
    </div>
  </div>
  <div v-else ref="slotWrapperEl" class="s-collapse-slot" :inert="!expanded">
    <slot v-if="hasBeenOpened" />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

const props = defineProps({
  expanded: {
    type: Boolean,
    default: false,
  },
  text: {
    type: String,
    default: "",
  },
});

const hasBeenOpened = ref(props.expanded);

const truncatedTextEl = useTemplateRef<HTMLElement>("truncatedTextEl");
const slotWrapperEl = useTemplateRef<HTMLElement>("slotWrapperEl");

let pendingTransitionEnd: ((event: TransitionEvent) => void) | null = null;

function elementIsHTMLElement(element: Element): element is HTMLElement {
  return element instanceof HTMLElement;
}

function applyExpandedState(el: HTMLElement, isExpanded: boolean) {
  if (pendingTransitionEnd) {
    el.removeEventListener("transitionend", pendingTransitionEnd);
    pendingTransitionEnd = null;
  }

  if (isExpanded) {
    el.style.height = `${el.scrollHeight}px`;
    el.style.opacity = "1";

    const onEnd = (event: TransitionEvent) => {
      // transitionend bubbles: a child's own transition must not end the expand early.
      if (event.target !== el) return;
      el.style.height = "auto";
      el.removeEventListener("transitionend", onEnd);
      pendingTransitionEnd = null;
    };
    pendingTransitionEnd = onEnd;
    el.addEventListener("transitionend", onEnd);
  } else {
    el.style.height = `${el.scrollHeight}px`;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.height = "0";
        el.style.opacity = "0.1";
      });
    });
  }
}

onMounted(() => {
  const el = slotWrapperEl.value;
  if (!el) return;

  if (props.expanded) {
    el.style.height = "auto";
    el.style.opacity = "1";
  } else {
    el.style.height = "0";
    el.style.opacity = "0.1";
    // Compute the collapsed state: an expand in this same tick would not transition → never auto.
    void el.offsetHeight;
  }
});

watch(
  () => props.expanded,
  async (isExpanded) => {
    if (isExpanded) {
      hasBeenOpened.value = true;
      await nextTick();
    }

    const el = slotWrapperEl.value;
    if (!el) return;

    applyExpandedState(el, isExpanded);
  },
);

function enter(element: Element) {
  if (!elementIsHTMLElement(element)) {
    return;
  }

  if (props.text && truncatedTextEl.value) {
    const truncatedHeight = `${truncatedTextEl.value.clientHeight}px`;
    element.style.width = getComputedStyle(element).width;
    element.style.position = "absolute";

    element.style.height = "auto";
    const height = getComputedStyle(element).height;
    element.style.height = truncatedHeight;
    truncatedTextEl.value.style.height = "0px";

    element.style.position = "";
    element.style.width = "";

    requestAnimationFrame(() => {
      element.style.height = height;
    });
  }
}

function afterEnter(element: Element) {
  if (!elementIsHTMLElement(element)) {
    return;
  }
  element.style.height = "auto";
}

function leave(element: Element) {
  if (!elementIsHTMLElement(element)) {
    return;
  }

  if (props.text && truncatedTextEl.value) {
    truncatedTextEl.value.style.height = "auto";
    const truncatedHeight = `${truncatedTextEl.value.clientHeight}px`;
    truncatedTextEl.value.style.height = "0px";
    element.style.height = getComputedStyle(element).height;

    requestAnimationFrame(() => {
      element.style.height = truncatedHeight;
    });
  }
}

function afterLeave() {
  if (props.text && truncatedTextEl.value) {
    truncatedTextEl.value.style.height = "auto";
  }
}
</script>

<style lang="postcss">
.s-collapse-text {
  &__content {
    @apply mb-2;

    p {
      @apply mb-0;
    }

    &--truncated {
      @apply line-clamp-3;
    }
  }

  &__show-more {
    @apply mt-2 text-base;
  }
}

.s-collapse-resize-enter-active,
.s-collapse-resize-leave-active {
  transition-property: height;
  transition-duration: 0.4s;
  @apply overflow-hidden ease-in-out;
}

.s-collapse-resize-enter-from,
.s-collapse-resize-leave-to {
  @apply h-0;
}

.s-collapse-slot {
  transition-property: height, opacity;
  transition-duration: 0.4s;
  @apply overflow-hidden ease-in-out;
}
</style>
